"use client";

import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { showToast } from "@/lib/toast";
import { Type, Globe } from "lucide-react";
import { useFont } from "@/hooks/useFont";

// Font options
const fontOptions = [
  {
    value: "system",
    label: "System Default",
    description: "Font mặc định của hệ thống (Vite.js)",
  },
  {
    value: "Inter",
    label: "Inter",
    description: "Font hiện đại, dễ đọc, được Google thiết kế",
  },
  {
    value: "Manrope",
    label: "Manrope",
    description: "Font mềm mại, thân thiện, geometric sans-serif",
  },
  {
    value: "Poppins",
    label: "Poppins",
    description: "Font tròn, hiện đại, geometric sans-serif",
  },
  {
    value: "Roboto",
    label: "Roboto",
    description: "Font Google chính thức, rõ ràng và dễ đọc",
  },
  {
    value: "Open Sans",
    label: "Open Sans",
    description: "Font mở, humanist sans-serif, dễ đọc",
  },
  {
    value: "Lato",
    label: "Lato",
    description: "Font sang trọng, thanh lịch, semi-rounded",
  },
  {
    value: "Montserrat",
    label: "Montserrat",
    description: "Font geometric, hiện đại, inspired by Buenos Aires",
  },
  {
    value: "Nunito",
    label: "Nunito",
    description: "Font tròn, thân thiện, well-balanced sans-serif",
  },
  {
    value: "Source Sans Pro",
    label: "Source Sans Pro",
    description: "Font Adobe, designed for UI, clean và readable",
  },
  {
    value: "Fira Sans",
    label: "Fira Sans",
    description: "Font Mozilla, humanist, designed for screens",
  },
  {
    value: "Noto Sans",
    label: "Noto Sans",
    description: "Font Google, hỗ trợ nhiều ngôn ngữ, clean",
  },
  {
    value: "Work Sans",
    label: "Work Sans",
    description: "Font modern, optimized cho digital interfaces",
  },
  {
    value: "IBM Plex Sans",
    label: "IBM Plex Sans",
    description: "Font IBM, corporate style, highly legible",
  },
  {
    value: "Playfair Display",
    label: "Playfair Display",
    description: "Font serif elegant, inspired by late 18th century",
  },
  {
    value: "Merriweather",
    label: "Merriweather",
    description: "Font serif, designed cho reading pleasure",
  },
];

export default function SettingsPage() {
  const [isUpdating, setIsUpdating] = useState(false);
  const { font, changeFont } = useFont();

  // Handle font change
  const handleFontChange = (newFont: string) => {
    changeFont(newFont);
  };

  // Update preferences
  const handleUpdatePreferences = async () => {
    setIsUpdating(true);

    try {
      showToast.success(
        "Cập nhật thành công",
        "Tùy chọn phông chữ đã được lưu"
      );
    } catch (error) {
      showToast.error("Lỗi", "Không thể lưu tùy chọn");
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-violet-600 to-fuchsia-600">
              Cài đặt hệ thống
            </h1>
            <div className="mt-2 flex items-center gap-2">
              <p className="text-gray-500 dark:text-gray-400">
                Quản lý tùy chọn giao diện và cài đặt hệ thống
              </p>
              <span className="inline-flex items-center rounded-full bg-blue-50 dark:bg-blue-900/20 px-2.5 py-0.5 text-xs font-medium text-blue-700 dark:text-blue-300 ring-1 ring-inset ring-blue-200 dark:ring-blue-800">
                <Globe className="h-3 w-3 mr-1" />
                Tiếng Việt
              </span>
            </div>
          </div>
        </div>

        <div className="grid gap-6">
          {/* Font Selection */}
          <Card className="shadow-lg border border-gray-100 dark:border-gray-700">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-b border-gray-100 dark:border-gray-700">
              <CardTitle className="flex items-center gap-3 text-gray-900 dark:text-white">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <Type className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <div className="text-lg font-semibold">Phông chữ</div>
                  <div className="text-sm font-normal text-gray-600 dark:text-gray-400">
                    Tùy chỉnh kiểu chữ cho toàn bộ giao diện
                  </div>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="space-y-3">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Chọn phông chữ
                </label>
                <Select value={font} onValueChange={handleFontChange}>
                  <SelectTrigger className="w-full h-12 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
                    <SelectValue placeholder="Chọn phông chữ" />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 max-h-80">
                    {fontOptions.map((option) => (
                      <SelectItem
                        key={option.value}
                        value={option.value}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <div className="flex flex-col w-full">
                          <div className="flex items-center justify-between w-full">
                            <div className="flex flex-col">
                              <span
                                className="font-medium"
                                style={{
                                  fontFamily:
                                    option.value === "system"
                                      ? '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                                      : `"${option.value}", sans-serif`,
                                }}
                              >
                                {option.label}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {option.description}
                              </span>
                            </div>
                            {option.value === "system" && (
                              <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-full">
                                Mặc định
                              </span>
                            )}
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Font Preview */}
              <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  Xem trước:
                </div>
                <div
                  className="text-lg"
                  style={{
                    fontFamily:
                      font === "system"
                        ? '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                        : `var(--font-family, "${font}"), sans-serif`,
                  }}
                >
                  Hệ thống quản trị phim hoạt hình
                </div>
                <div
                  className="text-sm text-gray-500 dark:text-gray-400 mt-1"
                  style={{
                    fontFamily:
                      font === "system"
                        ? '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                        : `var(--font-family, "${font}"), sans-serif`,
                  }}
                >
                  Phông chữ {font === "system" ? "System Default" : font} sẽ
                  được áp dụng cho toàn bộ giao diện
                </div>
              </div>

              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Thay đổi sẽ được áp dụng ngay lập tức</span>
              </div>
            </CardContent>
          </Card>

          {/* Language Info */}
          <Card className="shadow-lg border border-gray-100 dark:border-gray-700">
            <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-b border-gray-100 dark:border-gray-700">
              <CardTitle className="flex items-center gap-3 text-gray-900 dark:text-white">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <Globe className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <div className="text-lg font-semibold">Ngôn ngữ</div>
                  <div className="text-sm font-normal text-gray-600 dark:text-gray-400">
                    Ngôn ngữ hiển thị của giao diện
                  </div>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                    <span className="text-green-600 dark:text-green-400 font-bold text-lg">
                      VN
                    </span>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 dark:text-white">
                      Tiếng Việt
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Ngôn ngữ mặc định của hệ thống
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm font-medium text-green-600 dark:text-green-400">
                    Đang sử dụng
                  </span>
                </div>
              </div>

              <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
                <p>
                  Giao diện đang được hiển thị bằng tiếng Việt. Tất cả các thành
                  phần bao gồm menu, nút bấm, thông báo và nội dung đều được
                  dịch sang tiếng Việt để đảm bảo trải nghiệm người dùng tốt
                  nhất.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Update Button */}
        <div className="flex justify-end pt-4">
          <Button
            onClick={handleUpdatePreferences}
            disabled={isUpdating}
            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 py-2.5 text-sm font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
          >
            {isUpdating ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Đang cập nhật...
              </div>
            ) : (
              "Lưu cài đặt"
            )}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
